// Copyright 2021 Sorama B.V.

import { Grid } from "controls/plot/grid-plot/GridData";
import { Region } from "datatypes/SoramaLocalApi";
import { CameraProperties, Point3D } from "datatypes/SoramaWebApi";
import { Length } from "units/Length";
import { IVideoCalibration } from "./IVideoCalibration";

export class CameraPropertiesCalibration implements IVideoCalibration {
    private readonly aspectRatio: number;

    public readonly visibleRegion: Region;
    public readonly grid: Grid;
    public readonly targetPoints: Point3D[];

    constructor(
        private readonly position: Point3D,
        private readonly resolution: { width: number; height: number },
        private readonly distance: number,
        private readonly properties: CameraProperties
    ) {
        this.aspectRatio = resolution.width / resolution.height;
        this.targetPoints = this.computeTargetPoints();
        this.visibleRegion = this.computeVisibleRegion();

        this.visibleRegion = this.computeVisibleRegion();
        this.grid = this.computeGrid();
    }

    private computeVisibleRegion(): Region {
        let minx = Number.MAX_VALUE;
        let maxx = Number.MIN_VALUE;
        let miny = Number.MAX_VALUE;
        let maxy = Number.MIN_VALUE;

        let centerx = 0;
        let centery = 0;

        for (const point of this.targetPoints) {
            minx = Math.min(point.x, minx);
            maxx = Math.max(point.x, maxx);

            miny = Math.min(point.y, miny);
            maxy = Math.max(point.y, maxy);

            centerx += point.x;
            centery += point.y;
        }

        centerx /= this.targetPoints.length;
        centery /= this.targetPoints.length;

        return <Region>{
            centerX: centerx,
            centerY: centery,
            width: maxx - minx,
            height: maxy - miny,
        };
    }

    private computeTargetPoints(): Point3D[] {
        const targetPoints = <Point3D[]>[];
        const resX = this.resolution.width;
        const resY = this.resolution.height;

        for (let y = 0; y < resY; y++) {
            for (let x = 0; x < resX; x++) {
                targetPoints.push(
                    this.getProjectedPoint(
                        ((x + 0.5) / resX) * this.aspectRatio,
                        (y + 0.5) / resY,
                        this.properties,
                        this.position,
                        this.distance
                    )
                );
            }
        }

        return targetPoints;
    }

    private getProjectedPoint(
        u: number,
        v: number,
        properties: CameraProperties,
        sensorPosition: Point3D,
        distance: number
    ): Point3D {
        // This code is based on the C++ code in CameraProjectionMeshGridGeneral.cpp in mathlibrary-c repository
        const alignmentMatrix = properties.alignmentMatrix;

        // Now invert the camera matrix (matlab: c\[u;v;1])
        const xx = (u - properties.principalX) / properties.focalX;
        const yy = (v - properties.principalY) / properties.focalY;

        // Now determine the z component
        const z = Math.sqrt(Math.pow(distance, 2) / (Math.pow(xx, 2) + Math.pow(yy, 2) + 1));

        // Multiply with z and apply alignment matrix
        return {
            x: alignmentMatrix[0] * xx * z + alignmentMatrix[1] * yy * z + alignmentMatrix[2] * z,
            y: alignmentMatrix[3] * xx * z + alignmentMatrix[4] * yy * z + alignmentMatrix[5] * z,
            z: alignmentMatrix[6] * xx * z + alignmentMatrix[7] * yy * z + alignmentMatrix[8] * z,
        };
    }

    private computeGrid(): Grid {
        const resX = this.resolution.width;
        const resY = this.resolution.height;
        const width = this.visibleRegion.width;
        const height = this.visibleRegion.height;

        return new Grid(
            { x: resX, y: resY },
            { x: width / resX, y: height / resY },
            { x: this.visibleRegion.centerX, y: this.visibleRegion.centerY },
            Length.meter
        );
    }
}
